// SummitCaptureTabs.jsx
// Enhanced with facilitator guide content from all 10 session guides

import { useState } from "react";
import useSummitSync from "./useSummitSync";
import { OwnerInputSm, DueDateInputSm } from "./captureUtils";

const C = {
  dark: "#1a1a2e", gold: "#c9a84c", white: "#ffffff",
  border: "#e5e7eb", muted: "#6b7280", text: "#374151",
  green: "#27ae60", orange: "#e67e22", red: "#c0392b",
  blue: "#2980b9", yellow: "#f59e0b", yellowBg: "#fef3c7",
  light: "#f8f7f4", purple: "#8e44ad",
};

function Spinner() {
  return <div style={{ padding: 40, textAlign: "center", color: C.muted }}>Loading...</div>;
}

function SectionHeader({ title, subtitle }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.dark, textTransform: "uppercase", letterSpacing: 1 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{subtitle}</div>}
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, padding: 14, marginBottom: 12, ...style }}>
      {children}
    </div>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, padding: "10px 14px", textAlign: "center" }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || C.dark }}>{value}</div>
      <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 2 }}>{label}</div>
    </div>
  );
}

function FacilitatorNote({ text, color }) {
  return (
    <div style={{ background: (color || C.gold) + "18", border: "1px solid " + (color || C.gold) + "44", borderRadius: 6, padding: "8px 12px", marginBottom: 10, fontSize: 11, color: C.text, fontStyle: "italic" }}>
      <span style={{ fontWeight: 700, fontStyle: "normal", color: color || C.gold }}>Facilitator: </span>{text}
    </div>
  );
}

function WatchFor({ text }) {
  return (
    <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "7px 10px", marginBottom: 10, fontSize: 11, color: "#7f1d1d" }}>
      <span style={{ fontWeight: 700 }}>Watch for: </span>{text}
    </div>
  );
}

function ExpectedScore({ score, label }) {
  const cols = { 1: C.red, 2: C.orange, 3: C.yellow, 4: C.green, 5: "#1abc9c" };
  const c = cols[score] || C.muted;
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: c + "18", border: "1px solid " + c + "44", borderRadius: 6, padding: "4px 10px", marginBottom: 8, fontSize: 11 }}>
      <span style={{ fontWeight: 700, color: c }}>Expected: {score} - {label}</span>
    </div>
  );
}

export function SaveStatus({ status }) {
  if (!status) return null;
  const s = { saving: ["Saving...", C.orange], saved: ["Saved", C.green], error: ["Save failed", C.red] }[status];
  if (!s) return null;
  return <span style={{ fontSize: 11, color: s[1], marginLeft: 8 }}>{s[0]}</span>;
}

function csvDownload(filename, rows) {
  const csv = rows.map(function(r) { return r.map(function(c) { return '"' + String(c || "").replace(/"/g, '""') + '"'; }).join(","); }).join("\n");
  const a = document.createElement("a");
  a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
  a.download = filename;
  a.click();
}

// ─── AI READINESS SCORING ─────────────────────────────────────────────────────

const S3_DIMS = [
  {
    id: "strategy", label: "Strategy and Leadership", phase: "Phase 1",
    expectedScore: 2, expectedLabel: "At Risk",
    facilitatorPrompt: "Ask: who in this room owns AI outcomes? A strategy document is not a strategy. Push: is it funded, governed, and tied to P&L? The $500M outcome requires a named exec owner with budget and authority.",
    watchFor: "Room may score generously because a strategy document exists. An external reviewer would not call a document-only strategy above a 2.",
    anchors: { 1: "No AI strategy exists. No exec ownership.", 2: "Strategy on paper. No funding, owner, or governance.", 3: "Documented strategy with exec support. Budget in progress. Gaps in ownership.", 4: "Funded strategy. Named exec owner. Governance in place. Tied to business outcomes.", 5: "AI embedded in corporate strategy. Portfolio discipline applied. Stage-gating in place." },
    questions: ["Is there a written AI strategy tied to revenue and EBITDA targets?", "Does a named executive own AI outcomes with budget and authority?", "Are AI investments stage-gated based on demonstrated business value?", "Is there a governance model that can kill underperforming initiatives?"]
  },
  {
    id: "data", label: "Data Readiness", phase: "Phase 1",
    expectedScore: 1, expectedLabel: "Critical Gap",
    facilitatorPrompt: "Hardest score to defend but the right one. Walk through each checklist item: governed access, ETL, data quality, PII classification. None are in place. Having data in 29 systems is not governed access for AI.",
    watchFor: "Room will want a 2 because 'we have the data.' Every checklist item is not started. A 2 requires meaningful progress on at least one item.",
    anchors: { 1: "No governed data access. No ETL. No quality baseline. PII unclassified.", 2: "Some data accessible but ungoverned. No raw landing zone. No catalog.", 3: "ETL in progress. Quality measurement started. Governance framework drafted.", 4: "Governed ETL live. Quality scorecards running. PII classified. Catalog in place.", 5: "Semantic layer built. All data trusted, classified, auditable. AI-ready." },
    questions: ["Do we have governed access to the data required for AI use cases?", "Is a data governance framework documented and enforced?", "Are ETL pipelines, Raw Landing Zone, and a data catalog in place?", "Is data quality measured with scorecards and remediation workflows?", "Is PII identified and classified across all systems?"]
  },
  {
    id: "technology", label: "Technology and Infrastructure", phase: "Phase 1",
    expectedScore: 2, expectedLabel: "At Risk",
    facilitatorPrompt: "CData Connect AI is the current violation. A direct source-to-AI pipe with no governance. The warehouse decision made today changes this score. Microsoft stack strength is real but does not offset the ungoverned AI access.",
    watchFor: "The Microsoft stack (Teams, SharePoint, Azure, Power BI) will tempt a 3. The gap is ungoverned AI access, not the Microsoft tools.",
    anchors: { 1: "No warehouse. Ungoverned AI access. No RBAC. No audit trail.", 2: "Some infrastructure exists but ungoverned AI access is active. Warehouse not deployed.", 3: "Warehouse selected or in deployment. ETL starting. RBAC designed. CData replacement in progress.", 4: "Warehouse live. ETL operational. RBAC enforced. All AI reads governed layer only.", 5: "Semantic layer deployed. Full audit trail. All AI outputs traceable to source." },
    questions: ["Is a data warehouse deployed or selected with a firm start date?", "Does all AI access go through a governed semantic layer - not raw source systems?", "Is role-based access control designed and enforced at the AI query layer?", "Is every AI output auditable and traceable to its source data?", "Has CData Connect AI been replaced with a governed ETL solution?"]
  },
  {
    id: "talent", label: "Organisational Capability", phase: "Phase 2",
    expectedScore: 2, expectedLabel: "At Risk",
    facilitatorPrompt: "Distinguish between general operational capability (strong) and AI-specific capability (not in place). A strong ops culture does not mean the organisation is ready to absorb AI-generated outputs and act on them.",
    watchFor: "Rachel may push back on the culture question. The operational culture is genuinely strong. Acknowledge it, then redirect: AI requires a different capability than ops excellence.",
    anchors: { 1: "No AI skills. No training plan. No change management.", 2: "Isolated AI skills. No formal program. Change management not planned.", 3: "AI champions identified. Training in design. Change management started.", 4: "AI capability building underway. Formal training running. Change management structured.", 5: "AI fluency embedded across roles. Capability tracks defined. Active learning culture." },
    questions: ["Are there named AI champions in each business unit?", "Is a formal AI training and capability building program in place or planned?", "Is change management resourced and planned for AI adoption?", "Do business units have enough AI literacy to own their own use cases?", "Is there a hiring or upskilling plan for AI-specific skills?"]
  },
  {
    id: "governance", label: "Governance and Ethics", phase: "Phase 1",
    expectedScore: 1, expectedLabel: "Critical Gap",
    facilitatorPrompt: "Clearest score of the session. Nothing exists. CData Connect AI is running today without any governance framework. Let the room feel the weight of that, then commit to Day 2 output: a written framework with exec sign-off.",
    watchFor: "No pushback expected - the gaps are obvious. Focus the discussion forward: what must be written before pilot launch?",
    anchors: { 1: "No governance framework. No policies. No human-in-the-loop process. No acceptable use policy.", 2: "Governance principles discussed but nothing written. No enforcement.", 3: "Framework drafted. Key guardrails identified. Human-in-loop process designed.", 4: "Written framework with exec sign-off. RBAC enforced. Audit trail operational. Bias standards defined.", 5: "Full framework published, enforced, and audited. External reviewer would approve." },
    questions: ["Does a written AI governance framework exist with exec signatures?", "Are there written policies on bias, transparency, auditability, and privacy?", "Is a human-in-the-loop process documented for client-facing AI outputs?", "Is there an acceptable use policy covering what AI can and cannot do?", "Does AI access only the Semantic Layer - never raw source systems directly?"]
  },
  {
    id: "usecase", label: "Use-Case and Value Delivery", phase: "Phase 1",
    expectedScore: 2, expectedLabel: "At Risk",
    facilitatorPrompt: "Use cases are identified. The Day Planner is confirmed. But KPIs are not defined and change management is not planned. Prioritization happens in the next session - this score reflects where we are before that session runs.",
    watchFor: "Room may score this a 3 because use cases exist on paper. KPI definitions and a change management plan are required to score above 2. Neither exists yet.",
    anchors: { 1: "No use cases identified. No KPIs. No change management plan.", 2: "Use cases identified. Prioritization not complete. KPIs not defined. Change management not planned.", 3: "Use cases prioritized and ranked. KPIs in progress. Change management plan started.", 4: "Top use cases confirmed with owners. KPIs defined and signed off. Change management plan built.", 5: "Prioritized roadmap confirmed. KPIs defined. Change management ready for pilot launch." },
    questions: ["Have high-impact AI use cases been identified and prioritized by business value?", "Are success metrics and KPIs defined for each planned AI use case before build begins?", "Is there a roadmap from pilot to scale for priority use cases?", "Has a change management and user adoption plan been developed for the first AI deployments?"]
  }
];

const S3_GATE_CRITERIA = [
  { id: "g1", label: "Data Readiness score reaches 3 before Phase 2 ETL build begins", owner: "Martin Lopez" },
  { id: "g2", label: "Governance framework published and exec signed before pilot launch", owner: "Dan Frasco" },
  { id: "g3", label: "Technology score reaches 3 before warehouse deployment", owner: "Martin Lopez" },
  { id: "g4", label: "All Phase 1 gaps have assigned owners with due dates", owner: "Dan Frasco" },
  { id: "g5", label: "Data Readiness and Governance both reach 5 before CIM development begins (Q2 2027)", owner: "Vanessa Fucciani / Dan Frasco" },
  { id: "g6", label: "AI API Gateway Established", owner: "Martin Lopez" },
];

const S3_COLORS = { 1: C.red, 2: C.orange, 3: C.yellow, 4: C.green, 5: "#1abc9c" };
const S3_LABELS = { 1: "Critical Gap", 2: "At Risk", 3: "Developing", 4: "Strong", 5: "Best in Class" };

function defaultS3() {
  const d = {};
  S3_DIMS.forEach(function(dim) { d[dim.id] = { score: 0, gap: "", owner: "", action: "", due: "", presenter: "", agreed: false }; });
  S3_GATE_CRITERIA.forEach(function(g) { d["gate_" + g.id] = { met: false, notes: "" }; });
  d.overallNotes = "";
  d.heatMapConfirmed = false;
  return d;
}

export function S3ReadinessCapture({ projectConfig } = {}) {
  const { data: st, update, saveStatus, loaded, forceSave } = useSummitSync("s3", defaultS3(), projectConfig);
  const [expanded, setExpanded] = useState(null);

  function upd(dim, field, val) {
    update(function(p) { const n = { ...p }; n[dim] = { ...n[dim], [field]: val }; return n; });
  }

  const scored = S3_DIMS.filter(function(d) { return st[d.id]?.score > 0; }).length;
  const avg = scored > 0 ? (S3_DIMS.reduce(function(s, d) { return s + (st[d.id]?.score || 0); }, 0) / scored).toFixed(1) : "-";
  const gaps = S3_DIMS.filter(function(d) { return st[d.id]?.gap && st[d.id]?.owner; }).length;
  const agreed = S3_DIMS.filter(function(d) { return st[d.id]?.agreed; }).length;

  if (!loaded) return <Spinner />;

  return (
    <div>
      <FacilitatorNote color={C.blue}
        text="Score each dimension 1-5 as a live group exercise. No averaging - the room must agree. If two people disagree, let the discussion run 2-3 minutes then call the score. Score honestly: 3 means in progress with gaps, not nearly done. The Phase 4 gate requires 4 across all dimensions." />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px,1fr))", gap: 8, marginBottom: 16 }}>
        <StatBox label="Dimensions scored" value={scored + "/6"} color={scored === 6 ? C.green : C.orange} />
        <StatBox label="Avg score" value={avg} color={parseFloat(avg) >= 3 ? C.green : C.red} />
        <StatBox label="Gaps assigned" value={gaps + "/6"} color={gaps === 6 ? C.green : C.orange} />
        <StatBox label="Room agreed" value={agreed + "/6"} color={agreed === 6 ? C.green : C.orange} />
      </div>

      {S3_DIMS.map(function(dim) {
        const r = st[dim.id] || {};
        const sc = r.score ? S3_COLORS[r.score] : C.muted;
        const isOpen = expanded === dim.id;
        const showGap = r.score > 0 && r.score < 4;
        return (
          <Card key={dim.id} style={{ border: r.agreed ? "2px solid " + C.green : "1px solid " + C.border }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: C.dark }}>{dim.label}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{dim.phase}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: sc }}>{r.score || "-"}</div>
                {r.score > 0 && <div style={{ fontSize: 10, color: sc }}>{S3_LABELS[r.score]}</div>}
              </div>
            </div>
            <ExpectedScore score={dim.expectedScore} label={dim.expectedLabel} />
            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
              {[1,2,3,4,5].map(function(n) {
                return (
                  <button key={n} onClick={function() { upd(dim.id, "score", n); }}
                    style={{ flex: 1, padding: "8px 0", borderRadius: 4, border: r.score === n ? "2px solid " + S3_COLORS[n] : "1px solid " + C.border, background: r.score === n ? S3_COLORS[n] + "22" : C.white, color: S3_COLORS[n], fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                    {n}
                  </button>
                );
              })}
            </div>
            {r.score > 0 && (
              <div style={{ background: C.light, borderRadius: 4, padding: "6px 10px", marginBottom: 10, fontSize: 11, color: C.text }}>
                <strong style={{ color: sc }}>{r.score} - {S3_LABELS[r.score]}:</strong> {dim.anchors[r.score]}
              </div>
            )}
            <div style={{ marginBottom: 8 }}>
              <button onClick={function() { setExpanded(isOpen ? null : dim.id); }}
                style={{ fontSize: 11, color: C.blue, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}>
                {isOpen ? "Hide" : "Show"} scoring questions and facilitator notes
              </button>
            </div>
            {isOpen && (
              <div style={{ marginBottom: 12 }}>
                <FacilitatorNote text={dim.facilitatorPrompt} />
                <WatchFor text={dim.watchFor} />
                <div style={{ fontSize: 11, fontWeight: 700, color: C.dark, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Scoring questions</div>
                {dim.questions.map(function(q, i) {
                  return <div key={i} style={{ display: "flex", gap: 8, marginBottom: 5, fontSize: 11, color: C.text }}><span style={{ color: C.muted, flexShrink: 0 }}>{i+1}.</span><span>{q}</span></div>;
                })}
                <div style={{ marginTop: 10, fontSize: 11, fontWeight: 700, color: C.dark, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Score anchors</div>
                {[1,2,3,4,5].map(function(n) {
                  return <div key={n} style={{ display: "flex", gap: 6, marginBottom: 3, fontSize: 11 }}><span style={{ color: S3_COLORS[n], fontWeight: 700, width: 18, flexShrink: 0 }}>{n}</span><span style={{ color: C.text }}>{dim.anchors[n]}</span></div>;
                })}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              <div>
                <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Presenter for this dimension</div>
                <OwnerInputSm value={r.presenter || ""} onChange={function(e) { upd(dim.id, "presenter", e.target.value); }} placeholder="Who presents..." />
              </div>
              <div>
                <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Gap Owner</div>
                <OwnerInputSm value={r.owner || ""} onChange={function(e) { upd(dim.id, "owner", e.target.value); }} placeholder="Who closes this gap..." />
              </div>
            </div>
            {showGap && (
              <div>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Key Gap (score below 4 - required)</div>
                  <input value={r.gap || ""} onChange={function(e) { upd(dim.id, "gap", e.target.value); }}
                    placeholder="What specifically is missing or broken..."
                    style={{ width: "100%", padding: "6px 8px", border: "1px solid " + (r.gap ? C.border : C.orange), borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Action to Close Gap</div>
                    <input value={r.action || ""} onChange={function(e) { upd(dim.id, "action", e.target.value); }}
                      placeholder="Specific action required..."
                      style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Due Date</div>
                    <DueDateInputSm value={r.due || ""} onChange={function(e) { upd(dim.id, "due", e.target.value); }} />
                  </div>
                </div>
              </div>
            )}
            <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={!!r.agreed} onChange={function(e) { upd(dim.id, "agreed", e.target.checked); }} style={{ cursor: "pointer" }} />
              <label style={{ fontSize: 12, color: r.agreed ? C.green : C.muted, fontWeight: r.agreed ? 700 : 400, cursor: "pointer" }}>Room agreed on this score</label>
            </div>
          </Card>
        );
      })}

      <SectionHeader title="Phase Gate Criteria" subtitle="Confirm each gate. Every gate needs a named owner." />
      {S3_GATE_CRITERIA.map(function(g) {
        const r = st["gate_" + g.id] || {};
        return (
          <div key={g.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 0", borderBottom: "1px solid " + C.border }}>
            <input type="checkbox" checked={!!r.met} onChange={function(e) { upd("gate_" + g.id, "met", e.target.checked); }} style={{ marginTop: 2, cursor: "pointer" }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: r.met ? C.green : C.dark, fontWeight: r.met ? 700 : 400 }}>{g.label}</div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>Default owner: {g.owner}</div>
              <input value={r.notes || ""} onChange={function(e) { upd("gate_" + g.id, "notes", e.target.value); }}
                placeholder="Notes or conditions..."
                style={{ marginTop: 4, width: "100%", padding: "4px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 11, boxSizing: "border-box" }} />
            </div>
          </div>
        );
      })}

      <div style={{ marginTop: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <input type="checkbox" checked={!!st.heatMapConfirmed} onChange={function(e) { update(function(p) { return { ...p, heatMapConfirmed: e.target.checked }; }); }} style={{ cursor: "pointer" }} />
          <label style={{ fontSize: 12, color: st.heatMapConfirmed ? C.green : C.dark, fontWeight: st.heatMapConfirmed ? 700 : 400, cursor: "pointer" }}>
            Heat map reviewed as a group. Pattern acknowledged: 3 dimensions at 1 (Data, Technology, Governance), 3 at 2 (Strategy, Capability, Use-Case). This is not a failure - it is an accurate baseline.
          </label>
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Overall Readiness Notes</div>
        <textarea value={st.overallNotes || ""} onChange={function(e) { update(function(p) { return { ...p, overallNotes: e.target.value }; }); }}
          placeholder="Overall observations, surprises, and priorities from this session..." rows={3}
          style={{ width: "100%", padding: "8px 10px", border: "1px solid " + C.border, borderRadius: 6, fontSize: 12, boxSizing: "border-box", resize: "vertical" }} />
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        <button onClick={forceSave} style={{ padding: "8px 20px", background: C.dark, color: C.gold, border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 700 }}>Save</button>
        <button onClick={function() {
          const rows = [["Dimension", "Score", "Label", "Expected", "Presenter", "Gap Owner", "Key Gap", "Action", "Due", "Room Agreed"]];
          S3_DIMS.forEach(function(d) {
            const r = st[d.id] || {};
            rows.push([d.label, r.score || "", r.score ? S3_LABELS[r.score] : "", d.expectedScore + " - " + d.expectedLabel, r.presenter || "", r.owner || "", r.gap || "", r.action || "", r.due || "", r.agreed ? "Yes" : "No"]);
          });
          csvDownload("TCC_AI_Readiness_Scores.csv", rows);
        }} style={{ padding: "8px 16px", background: C.white, color: C.dark, border: "1px solid " + C.border, borderRadius: 6, cursor: "pointer", fontSize: 12 }}>
          Export CSV
        </button>
        <SaveStatus status={saveStatus} />
      </div>
    </div>
  );
}

